import { useIsAuthenticated } from "@azure/msal-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthenticatedFetch from "./authenticated";
import { AUTH_BYPASS } from "./bypass";
import { fetchCurrentUser } from "./fetchCurrentUser";
import {
  clearPostLoginPath,
  pathFromLocation,
  resolvePostLoginTarget,
  savePostLoginPath,
} from "./postLoginRedirect";
import {
  clearStoredMsAccount,
  getStoredAccountIdentifiers,
  hasStoredMsSession,
} from "./msStorage";
import { User } from "../models/User";
import { FEEDBACK } from "../models/Feedback";

export function useAppSession() {
  const navigate = useNavigate();
  const location = useLocation();
  const getAccessHeader = useAuthenticatedFetch();

  const msalAuthenticated = useIsAuthenticated(getStoredAccountIdentifiers());
  const isAuthenticated = AUTH_BYPASS || msalAuthenticated;

  const [user, setUser] = useState<User | null>(null);
  const [fetchUser, setFetchUser] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [showFeedback, setShowFeedback] = useState<FEEDBACK>(FEEDBACK.NONE);

  useEffect(() => {
    if (!isAuthenticated && !AUTH_BYPASS) {
      savePostLoginPath(pathFromLocation(location));
    }
  }, [isAuthenticated, location]);

  const completeAppLogin = useCallback(
    async (fromLoginButton = false) => {
      if (!AUTH_BYPASS && !hasStoredMsSession()) {
        console.log("User logged out in different tab or window.");
        savePostLoginPath();
        navigate("/", { replace: true });
        return;
      }

      setLoginError(false);
      setFetchUser(true);
      setShowFeedback(FEEDBACK.NONE);

      const appUser = await fetchCurrentUser(getAccessHeader);
      setFetchUser(false);

      if (!appUser) {
        setLoginError(true);
        setShowFeedback(FEEDBACK.ERROR);
        return;
      }

      setUser(appUser);

      const target = resolvePostLoginTarget(
        appUser,
        location.pathname,
        fromLoginButton || AUTH_BYPASS,
      );
      if (target) {
        navigate(target, { replace: true });
      }
    },
    [getAccessHeader, location.pathname, navigate],
  );

  useEffect(() => {
    if (isAuthenticated && !user) {
      completeAppLogin().then();
    }
    // Only re-sync when MS auth state changes, not on every user update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const logout = useCallback(async () => {
    try {
      const response = await fetch("/api/logout", {
        method: "PUT",
        headers: await getAccessHeader(),
      });

      if (!response.ok) {
        const data = await response.json();
        setShowFeedback(FEEDBACK.ERROR);
        console.error("Logout error: ", data.error);
        return;
      }

      setUser(null);
      clearStoredMsAccount();
      clearPostLoginPath();
      navigate("/", { replace: true });
    } catch (error) {
      setShowFeedback(FEEDBACK.ERROR);
      console.error(error);
    }
  }, [getAccessHeader, navigate]);

  return {
    user,
    fetchUser,
    loginError,
    showFeedback,
    setShowFeedback,
    isAuthenticated,
    completeAppLogin,
    logout,
  };
}
